import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthenticationService } from './authentication/services/authentication.service';
import { PcnGuard } from './shared/common/services/pcn-guard.service';
import { AdminGuard } from './layouts/services/admin-guard.service';
import { AuthGuard } from './authentication/guard/auth-guard.service';
import { AuthLayoutComponent } from './layouts/auth/auth-layout.component';
import { LoginComponent } from './authentication/components/login.component';
import { RegisterComponent } from './authentication/components/register.component';
import { PageNotFoundComponent } from './shared/common/components/page-not-found/pagenotfound.component';
import { AppLayoutComponent } from './layouts/app/app-layout.component';
import { AdminLayoutComponent } from './layouts/admin/admin-layout.component';
import { UsersListComponent } from './layouts/admin/users/users-list.component';
import { WelcomeLayoutComponent } from './layouts/app/welcome/welcome-layout.component';
import { ReasonsLayoutComponent } from './layouts/app/reasons/reasons-layout.component';
import { AddUserComponent } from './shared/common/components/add-user/add-user.component';
import { PoliciesListComponent } from './layouts/admin/policies/policies-list.component';
import { AlertsListComponent } from './layouts/admin/alerts/alerts-list.component';
import { AddAlertComponent } from './shared/common/components/add-alert/add-alert.component';
import { HelpComponent } from './shared/common/components/help/help.component';
import { TermsComponent } from './shared/common/components/terms/terms.component';
import { ReportsLayoutComponent } from './layouts/reports/reports-layout.component';
import { DepartmentReportsComponent } from './shared/common/components/department-reports/department-reports.component';
import { CustomReportsComponent } from './shared/common/components/custom-reports/custom-reports.component';
import { BacklogReportsComponent } from './shared/common/components/backlog-reports/backlog-reports.component';
import { TeamReportsComponent } from './shared/common/components/team-reports/team-reports.component';
import { TeamsReportsComponent } from './shared/common/components/teams-reports/teams-reports.component';
import { SettingsComponent } from './layouts/admin/settings/settings.component';
import { AdminAccessGuard } from './layouts/services/admin-access-guard.service';
import { ReportsAccessGuard } from './layouts/services/reports-access-guard.service';
import { UserGuard } from './layouts/services/user-guard.service';
import { ConfigurationComponent } from './layouts/admin/configuration/configuration.component';
import { PlaygroundComponent } from './layouts/admin/playground/playground.component';
import { LetterManagementComponent } from '././shared/common/components/letter-management/letter-management.component';
import { AdditionalExplanationComponent } from './layouts/admin/additionalExplanation/additionalExplanation.component';

/* account related components */
import { PasswordRequestComponent } from './authentication/components/password-request.component';
import { PasswordRequestConfirmationComponent } from './authentication/components/password-request-confirmation.component';
import { PasswordResetComponent } from './authentication/components/password-reset.component';
import { AccountUnlockedConfirmationComponent } from './authentication/components/account-unlocked-confirmation.component';
import { AccountActivationComponent } from './authentication/components/activation-request.component';
import { MfaComponent } from './authentication/components/mfa.component';
import { NexusComponent } from './layouts/admin/nexus/nexus.component';
import { UserSettingsComponent } from './shared/common/components/user-settings/user-settings.component';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: '',
    component: AuthLayoutComponent,
    children: [
      {
        path: 'login',
        component: LoginComponent,
        data: { title: 'Login' },
      },
      {
        path: 'login/:id',
        component: LoginComponent,
        data: { title: 'Login' },
      },
      {
        path: 'password-request',
        component: PasswordRequestComponent,
        data: { title: 'Request Password Reset' },
      },
      {
        path: 'password-request-confirmation',
        component: PasswordRequestConfirmationComponent,
        data: { title: 'Password Reset Link Sent' },
      },
      {
        path: 'password-reset',
        component: PasswordResetComponent,
        data: { title: 'Change your password' },
      },
      {
        path: 'unlock-account',
        component: AccountUnlockedConfirmationComponent,
        data: { title: 'Unlock account' },
      },
      {
        path: 'activate-account',
        component: AccountActivationComponent,
        data: { title: 'Activate your account' },
      },
      {
        path: 'mfa',
        component: MfaComponent,
        data: { title: 'MFA Verification Required' },
      },
    ],
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [AuthGuard, UserGuard],
    children: [
      {
        path: 'pcn-details',
        component: WelcomeLayoutComponent,
        data: { title: 'Pcn Details' },
      },
      {
        path: 'reasons',
        canActivate: [PcnGuard],
        component: ReasonsLayoutComponent,
        data: { title: 'Reasons' },
      },
      {
        path: 'help',
        component: HelpComponent,
        data: { title: 'Help' },
      },
      {
        path: 'terms-conditions',
        component: TermsComponent,
        data: { title: 'Terms & Conditions' },
      },
      {
        path: 'letter-management',
        component: LetterManagementComponent,
        data: { title: 'Letter Management' },
      },
      {
        path: 'user-settings',
        component: UserSettingsComponent,
        data: { title: 'User - Settings' },
      },
    ],
  },
  {
    path: 'admin',
    component: AdminLayoutComponent,
    canActivate: [AuthGuard, AdminAccessGuard],
    children: [
      {
        path: 'users',
        children: [
          {
            path: '',
            component: UsersListComponent,
            data: { title: 'View Users' },
          },
          {
            path: 'add',
            component: AddUserComponent,
            data: { title: 'Add New User' },
          },
          {
            path: 'edit/:id',
            component: AddUserComponent,
            data: { title: 'edit User' },
          },
        ],
      },
      {
        path: 'policies',
        component: PoliciesListComponent,
        data: { title: 'Policies' },
      },

      {
        path: 'alerts',
        children: [
          {
            path: '',
            component: AlertsListComponent,
            data: { title: 'Alerts' },
          },
          {
            path: 'add',
            component: AddAlertComponent,
            data: { title: 'Add New Alert' },
          },
          {
            path: 'edit/:id',
            component: AddAlertComponent,
            data: { title: 'Alert Details' },
          },
        ],
      },
      {
        path: 'settings',
        component: SettingsComponent,
        canActivate: [AdminAccessGuard],
        data: { title: 'Admin - Settings' },
      },
      {
        path: 'nexus',
        component: NexusComponent,
        canActivate: [AdminAccessGuard],
        data: { title: 'Admin - Nexus' },
      },
      {
        path: 'configuration',
        component: ConfigurationComponent,
        canActivate: [AdminGuard],
        data: { title: 'Admin - Configuration' },
      },
      {
        path: 'playground',
        component: PlaygroundComponent,
        canActivate: [AdminGuard],
        data: { title: 'Admin - Playground' },
      },
      {
        path: 'additionalExplanation',
        component: AdditionalExplanationComponent,
        data: { title: 'Admin - Additional Explanation' },
      },
    ],
  },
  {
    path: 'reports',
    component: ReportsLayoutComponent,
    canActivate: [AuthGuard, ReportsAccessGuard],
    children: [
      {
        path: 'department',
        component: DepartmentReportsComponent,
        data: { title: 'Department Performance' },
      },
      {
        path: 'team',
        children: [
          {
            path: '',
            component: TeamsReportsComponent,
            data: { title: 'Teams Performance' },
          },
          {
            path: ':teamId',
            component: TeamReportsComponent,
            data: { title: 'Team Performance' },
          },
        ],
      },
      {
        path: 'backlog',
        component: BacklogReportsComponent,
        data: { title: 'Backlog Performance' },
      },
      {
        path: 'custom',
        component: CustomReportsComponent,
        data: { title: 'Custom Performance' },
      },
    ],
  },
  {
    path: '**',
    // component: PageNotFoundComponent,
    redirectTo: 'pcn-details',
    pathMatch: 'full',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule {}
